import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useInvoiceStore } from '../../../store/invoiceStore';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { LoadingPage } from '../../../components/Loading';
import toast from 'react-hot-toast';

export function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentInvoice, fetchInvoice, deleteInvoice, sendInvoice, recordPayment, isLoading } = useInvoiceStore();

  useEffect(() => {
    if (id) {
      fetchInvoice(id);
    }
  }, [id]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await deleteInvoice(id!);
        toast.success('Invoice deleted successfully');
        navigate('/money/invoices');
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to delete invoice');
      }
    }
  };

  const handleSend = async () => {
    try {
      await sendInvoice(id!);
      toast.success('Invoice sent successfully');
      fetchInvoice(id!);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send invoice');
    }
  };

  const handleRecordPayment = async () => {
    try {
      await recordPayment(id!, {
        amount: currentInvoice?.totalAmount,
        paymentDate: new Date().toISOString(),
        paymentMethod: 'bank_transfer',
      });
      toast.success('Payment recorded successfully');
      fetchInvoice(id!);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to record payment');
    }
  };

  if (isLoading || !currentInvoice) {
    return <LoadingPage message="Loading invoice..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-muted-foreground font-mono">{currentInvoice.invoiceNumber}</span>
            <Badge variant={
              currentInvoice.status === 'paid' ? 'success' :
              currentInvoice.status === 'overdue' ? 'error' :
              currentInvoice.status === 'sent' ? 'info' : 'default'
            }>
              {currentInvoice.status}
            </Badge>
          </div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Invoice Details</h1>
        </div>
        <div className="flex gap-3">
          {currentInvoice.status === 'draft' && (
            <Button variant="primary" onClick={handleSend}>Send Invoice</Button>
          )}
          {currentInvoice.status !== 'paid' && (
            <Button variant="success" onClick={handleRecordPayment}>Record Payment</Button>
          )}
          <Button variant="secondary" onClick={() => navigate(`/money/invoices/${id}/edit`)}>Edit</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </div>
      </div>

      {/* Invoice Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h2 className="text-foreground font-display text-section-title">Customer</h2>
          </CardHeader>
          <CardContent>
            <p className="text-foreground text-lg">{currentInvoice.customerName}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-foreground font-display text-section-title">Dates</h2>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2">
              <div>
                <dt className="text-sm text-muted-foreground">Issued Date</dt>
                <dd className="text-foreground">{new Date(currentInvoice.issuedDate).toLocaleDateString()}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Due Date</dt>
                <dd className="text-foreground">{new Date(currentInvoice.dueDate).toLocaleDateString()}</dd>
              </div>
              {currentInvoice.paidAt && (
                <div>
                  <dt className="text-sm text-muted-foreground">Paid Date</dt>
                  <dd className="text-foreground">{new Date(currentInvoice.paidAt).toLocaleDateString()}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Amount Summary */}
      <Card>
        <CardHeader>
          <h2 className="text-foreground font-display text-section-title">Amount Summary</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Subtotal</p>
              <p className="text-foreground font-display text-metric">${(currentInvoice.totalAmount - currentInvoice.taxAmount).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Tax</p>
              <p className="text-foreground font-display text-metric">${currentInvoice.taxAmount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total</p>
              <p className="text-foreground font-display text-metric">${currentInvoice.totalAmount.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions Timeline */}
      <Card>
        <CardHeader>
          <h2 className="text-foreground font-display text-section-title">Actions</h2>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Link to={`/money/invoices/${id}/edit`}>
              <Button variant="secondary" className="w-full">Edit Invoice</Button>
            </Link>
            <Link to={`/money/invoices/${id}/duplicate`}>
              <Button variant="secondary" className="w-full">Duplicate Invoice</Button>
            </Link>
            <Button variant="secondary" className="w-full" onClick={() => window.print()}>
              Print Invoice
            </Button>
            <Button variant="secondary" className="w-full">
              Download PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
